import { DocumentLinkListState } from './document-link-list-state';
import { DocumentLinkSelectionState } from './document-link-selection-state';
import { DocumentLinkUploadFlow } from './document-link-upload-flow';
import { DocumentLinkSidebarContext } from './document-link-sidebar-context';
import { CommonModule } from '@angular/common';
import { Component, DestroyRef, ElementRef, OnInit, OnDestroy, ViewChild, inject, signal } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatIconModule } from '@angular/material/icon';
import { PageEvent } from '@angular/material/paginator';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatTooltipModule } from '@angular/material/tooltip';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';
import { Document, DocumentUsage } from '../model';
import { CompanyService } from '../common/service/company.service';
import { DocumentTypeService } from '../common/service/document-type.service';
import { DocumentService } from '../common/service/document.service';
import { Dialog } from '../common/dialog.service';
import { ManagedDocument } from '../common/models/document.model';
import { TranslateModule } from '@ngx-translate/core';
import { Location } from '@angular/common';
import { MatMenuModule } from '@angular/material/menu';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { RemoteDocumentService } from './remote-document.service';
import { ConfirmDeleteDialogComponent } from '../common/confirm-delete-dialog.component';
import { DocumentUploadService } from './document-upload.service';
import { clearDocumentLinkSidebarState } from './document-link-context.util';
import { DocumentLinkUploadContextService } from './document-link-upload-context.service';
import { CanvasStateService } from './canvas-state.service';
import { DocumentCanvasComponent } from './document-canvas.component';
import { AppState } from '../common';
import { CompanyWriteAccess } from '../common/service/company-write-access.service';
import { NotificationService } from '../common/service/notification.service';

import type { DocumentLinkDialogData, SortField, DocumentCategory, DocumentLinkUrlState } from './document-link.model';
@Component({
  selector: 'app-document-link-dialog',
  templateUrl: 'document-link-dialog.component.html',
  styleUrl: 'document-link-dialog.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatCheckboxModule,
    MatSlideToggleModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatPaginatorModule,
    MatTooltipModule,
    MatMenuModule,
    TranslateModule,
    NgxMatSelectSearchModule,
  ],
})
/** Coordinates the linking dialog; catalog, selection, upload and restoration state have separate owners. */
export class DocumentLinkDialogComponent implements OnInit, OnDestroy {
  /** Mirrors the server's `Company.mayWrite` — see `CompanyWriteAccess`. */
  readonly readOnly = inject(CompanyWriteAccess).readOnly;

  private readonly dialogRef = inject(MatDialogRef<DocumentLinkDialogComponent>);
  private readonly dialog = inject(MatDialog);
  private readonly dialogService = inject(Dialog);
  private readonly data = inject<DocumentLinkDialogData>(MAT_DIALOG_DATA);
  private readonly documentService = inject(DocumentService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly remoteService = inject(RemoteDocumentService);
  private readonly appState = inject(AppState);
  private readonly canvasState = inject(CanvasStateService);
  private readonly linkUploadContext = inject(DocumentLinkUploadContextService);
  protected readonly list: DocumentLinkListState = new DocumentLinkListState(
    this.data,
    inject(FormBuilder),
    inject(CompanyService),
    inject(DocumentTypeService),
    this.documentService,
    {
      onDocumentsLoaded: () => {
        this.maybeAutoAttachDocument();
        this.maybeAutoPreviewDocument();
      },
      loadDocuments: (page, size) => this.loadDocuments(page, size),
    }
  );
  protected readonly selection: DocumentLinkSelectionState = new DocumentLinkSelectionState(
    this.data,
    this.canvasState,
    { documents: () => this.list.documents() }
  );
  private readonly uploadFlow = new DocumentLinkUploadFlow(
    this.dialog,
    this.documentService,
    inject(NotificationService),
    inject(DocumentUploadService),
    {
      enforcedCategory: () => this.list.enforcedCategory(),
      reloadDocuments: () => this.loadDocuments(this.pageIndex(), this.pageSize()),
      selectUploadedDocument: (id) => this.selection.selectUploadedDocument(id),
    }
  );
  private readonly sidebarContext = new DocumentLinkSidebarContext(this.data, inject(Location));
  private readonly autoAttachId = this.data.linkedDocumentId ?? null;
  private restorePreviewDocumentId = this.data.restorePreviewDocumentId ?? null;
  private readonly closing = signal(false);
  private closeTimer: number | null = null;
  private autoAttachHandled = false;
  @ViewChild('uploadInput') uploadInput?: ElementRef<HTMLInputElement>;
  readonly enforcedCategory = this.list.enforcedCategory;
  readonly filters = this.list.filters;
  readonly documents = this.list.documents;
  readonly previewMap = this.list.previewMap;
  readonly total = this.list.total;
  readonly pageIndex = this.list.pageIndex;
  readonly pageSize = this.list.pageSize;
  readonly sortField = this.list.sortField;
  readonly sortDirection = this.list.sortDirection;
  readonly filterState = this.list.filterState;
  readonly documentTypes = this.list.documentTypes;
  readonly typesLoading = this.list.typesLoading;
  readonly typeSearchControl = this.list.typeSearchControl;
  readonly typeSearchActive = this.list.typeSearchActive;
  readonly typeGroups = this.list.typeGroups;
  readonly allowedCategories = this.list.allowedCategories;
  readonly filtersApplied = this.list.filtersApplied;
  readonly selectedDocuments = this.selection.selectedDocuments;
  readonly selectedCount = this.selection.selectedCount;
  readonly groupsEnabled = this.selection.groupsEnabled;
  readonly groupedDocuments = this.selection.groupedDocuments;
  readonly ungroupedDocuments = this.selection.ungroupedDocuments;
  readonly expandedUsageDocuments = this.selection.expandedUsageDocuments;
  constructor() {
    this.list.bindTypeSearch(this.destroyRef);
    this.dialogRef
      .backdropClick()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        void this.closeWithAnimation();
      });
    this.dialogRef
      .keydownEvents()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          void this.closeWithAnimation();
        }
      });
  }

  ngOnInit(): void {
    this.canvasState.setStorageKey(this.canvasStorageKey());
    this.linkUploadContext.registerUploadHandler((files) => this.handleUploadFiles(files));
    this.selection.refreshPendingDocumentIds();
    this.sidebarContext.initialize();
    this.list.initializeFilters(this.destroyRef);
    void this.initialize();
  }

  ngOnDestroy(): void {
    this.linkUploadContext.registerUploadHandler(null);
    if (this.closeTimer !== null) {
      window.clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
    this.sidebarContext.destroy();
  }

  protected async initialize() {
    await Promise.all([this.loadDocumentTypes(), this.loadDocuments()]);
  }

  private canvasStorageKey() {
    const companyId = this.data.company?.id;
    const siteId = this.data.site?.id;
    return companyId && siteId ? `document-canvas:${companyId}:${siteId}` : null;
  }

  protected openUploadPicker(input: HTMLInputElement) {
    input.value = '';
    input.click();
  }

  protected async handleUploadSelection(event: Event) {
    const target = event.target as HTMLInputElement;
    const files = target.files ? Array.from(target.files) : [];
    target.value = '';
    await this.handleUploadFiles(files);
  }

  protected handleTableRowSelect(row: Document | ManagedDocument) {
    if (this.isServerDocument(row)) {
      this.selectDocument(row);
    }
  }

  protected handleTableRowView(row: Document | ManagedDocument) {
    if (this.isServerDocument(row)) {
      void this.viewDocument(row);
    }
  }

  protected clearFilters() {
    this.resetFiltersToDefault(true);
  }

  protected async viewDocument(document: Document) {
    try {
      this.updateLinkingUrlState({ linkDialog: 'preview', documentId: document.id });
      const response = await this.documentService.view(document.id, undefined, {
        name: document.title ?? document.fileName ?? document.id,
      });
      if (!response?.body) {
        return;
      }
      const { body } = response;
      await this.dialogService.showPdf(document.title ?? 'Document', body, {
        fileName: document.fileName ?? `${document.title ?? 'document'}.pdf`,
        mimeType: document.fileType ?? body.type,
      });
    } catch (error) {
      console.error('Failed to view document', error);
    } finally {
      this.updateLinkingUrlState({ linkDialog: null, documentId: null });
    }
  }

  protected async editDocument(document: Document, event: Event) {
    event.stopPropagation();
    const updated = await this.remoteService.editUploadedDocument(document, {
      enforcedCategory: this.enforcedCategory(),
    });
    if (updated) {
      await this.loadDocuments(this.pageIndex(), this.pageSize());
    }
  }

  protected async deleteDocument(document: Document, event: Event) {
    event.stopPropagation();
    const confirmed = await this.confirmDocumentDeletion(document.title);
    if (!confirmed) {
      return;
    }
    await this.remoteService.deleteUploadedDocument(document);
    this.selectedDocuments.update((current) => {
      const next = new Map(current);
      next.delete(document.id);
      return next;
    });
    await this.loadDocuments(this.pageIndex(), this.pageSize());
  }

  protected closeDialog() {
    void this.closeWithAnimation();
  }

  protected showCanvas() {
    const activeTypeId = this.filterState().typeId || null;
    const categoryFilter = this.extractCategoryFilter(activeTypeId);
    const canvasRef = this.dialog.open(DocumentCanvasComponent, {
      data: {
        documents: this.documents(),
        previewMap: this.previewMap(),
        storageKey: this.canvasStorageKey(),
        selectionMode: true,
        selectedDocumentIds: Array.from(this.selectedDocuments().keys()),
        category: categoryFilter ?? this.enforcedCategory(),
        documentTypeId: categoryFilter ? null : activeTypeId,
      },
      width: '100vw',
      height: '100dvh',
      maxWidth: '100vw',
      maxHeight: '100dvh',
      panelClass: 'document-canvas-dialog',
      autoFocus: false,
      restoreFocus: false,
    });
    canvasRef.afterClosed().subscribe((result?: { selectedDocumentIds?: string[] }) => {
      const selectedIds = result?.selectedDocumentIds ?? [];
      const documentsById = new Map(this.documents().map((document) => [document.id, document]));
      this.selectedDocuments.set(
        new Map(
          selectedIds
            .map((documentId) => [documentId, documentsById.get(documentId)])
            .filter((entry): entry is [string, Document] => Boolean(entry[1]))
        )
      );
      void this.loadDocuments(this.pageIndex(), this.pageSize());
    });
  }

  protected confirm() {
    const selected = Array.from(this.selectedDocuments().values());
    if (!selected.length) {
      return;
    }
    void this.closeWithAnimation(selected);
  }

  protected previewSrc(document: Document) {
    const preview = this.previewMap()[document.id]?.preview;
    return preview ? `data:image/png;base64,${preview}` : null;
  }

  protected hasPreviewError(document: Document) {
    return !!this.previewMap()[document.id]?.error_type;
  }

  protected handleUsageClick(usage: DocumentUsage, event: Event) {
    event.stopPropagation();
    const cs = this.appState.companySite$.value;
    if (!cs?.company) return;
    const companyId = cs.company.id;
    const siteId = cs.site?.id ?? cs.company.sites?.[0]?.id;
    if (!siteId) return;
    window.open(`/company/${companyId}/site/${siteId}/${usage.link}`, '_blank', 'noopener');
  }

  private async confirmDocumentDeletion(name?: string) {
    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '420px',
      data: {
        name: name ?? '',
        type: '',
      },
    });
    return firstValueFrom(dialogRef.afterClosed());
  }

  private closeWithAnimation(result?: Document[]) {
    if (this.closing()) {
      clearDocumentLinkSidebarState();
      return Promise.resolve();
    }
    this.closing.set(true);
    this.dialogRef.addPanelClass('document-link-sidebar-panel-closing');
    return new Promise<void>((resolve) => {
      this.closeTimer = window.setTimeout(() => {
        this.closeTimer = null;
        this.dialogRef.close(result);
        clearDocumentLinkSidebarState();
        resolve();
      }, 180);
    });
  }

  private maybeAutoAttachDocument() {
    if (this.autoAttachHandled || this.restorePreviewDocumentId || !this.autoAttachId) {
      return;
    }
    const doc = this.documents().find((document) => document.id === this.autoAttachId);
    if (!doc) {
      return;
    }
    this.autoAttachHandled = true;
    this.selectedDocuments.set(new Map([[doc.id, doc]]));
    setTimeout(() => this.confirm());
  }

  private maybeAutoPreviewDocument() {
    if (!this.restorePreviewDocumentId) {
      return;
    }
    const doc = this.documents().find((document) => document.id === this.restorePreviewDocumentId);
    if (!doc) {
      return;
    }
    this.restorePreviewDocumentId = null;
    void this.viewDocument(doc);
  }

  private isServerDocument(row: Document | ManagedDocument): row is Document {
    const managed = row as ManagedDocument;
    return managed.dataUrl === undefined && managed.blob === undefined;
  }
  protected loadDocumentTypes() {
    return this.list.loadDocumentTypes();
  }
  protected loadDocuments(page = this.pageIndex(), size = this.pageSize()) {
    return this.list.loadDocuments(page, size);
  }
  protected loadPreviews(documents: Document[]) {
    return this.list.loadPreviews(documents);
  }
  protected handlePageChange(event: PageEvent) {
    return this.list.handlePageChange(event);
  }
  protected changeSort(field: SortField) {
    return this.list.changeSort(field);
  }
  protected categoryFilterValue(category: DocumentCategory) {
    return this.list.categoryFilterValue(category);
  }
  protected extractCategoryFilter(value: string | null | undefined): DocumentCategory | null {
    return this.list.extractCategoryFilter(value);
  }
  protected resetFiltersToDefault(triggerLoad: boolean) {
    return this.list.resetFiltersToDefault(triggerLoad);
  }
  protected documentUsages(document: Document) {
    return this.selection.documentUsages(document);
  }
  protected usageCount(document: Document) {
    return this.selection.usageCount(document);
  }
  protected usageCountLabel(document: Document) {
    return this.selection.usageCountLabel(document);
  }
  protected isUsageExpanded(document: Document) {
    return this.selection.isUsageExpanded(document);
  }
  protected toggleUsageList(document: Document, event: Event) {
    return this.selection.toggleUsageList(document, event);
  }
  protected selectDocument(document: Document) {
    return this.selection.selectDocument(document);
  }
  protected isGroupFullySelected(documents: Document[]) {
    return this.selection.isGroupFullySelected(documents);
  }
  protected isGroupPartiallySelected(documents: Document[]) {
    return this.selection.isGroupPartiallySelected(documents);
  }
  protected toggleGroup(documents: Document[], checked: boolean) {
    return this.selection.toggleGroup(documents, checked);
  }
  protected isSelected(document: Document) {
    return this.selection.isSelected(document);
  }
  protected hasUsages(document: Document) {
    return this.selection.hasUsages(document);
  }
  protected usageIcon(usage: DocumentUsage): string {
    return this.selection.usageIcon(usage);
  }
  protected usageLabel(usage: DocumentUsage): string {
    return this.selection.usageLabel(usage);
  }
  protected isPendingDocument(document: Document) {
    return this.selection.isPendingDocument(document);
  }
  protected handleUploadFiles(files: File[]) {
    return this.uploadFlow.handleUploadFiles(files);
  }
  private updateLinkingUrlState(state: DocumentLinkUrlState) {
    this.sidebarContext.update(state);
  }
}
